import { createResource, createSignal, Show } from "solid-js";
import type { SkullOwnerNbt } from "../models/itemStack";
import { resolveSkin } from "../services/skullTexture";
import { isFeatureEnabled } from "../services/featureFlags";
import Modal from "./Modal";
import Text from "./Text";
import SkinViewer3D from "./SkinViewer3D";
import { useToast } from "./MessageBox";
import { useTranslation } from "../hooks/useTranslation";
import styles from "./SkullOwnerView.module.css";

const [owner, setOwner] = createSignal<SkullOwnerNbt | null>(null);

export const SKULL_VIEWER_FLAG = "SKULL_VIEWER";

export function showSkullOwner(target: SkullOwnerNbt): void {
  if (!isFeatureEnabled(SKULL_VIEWER_FLAG)) return;
  setOwner({ ...target });
}

export async function downloadSkin(skinUrl: string, name: string): Promise<void> {
  try {
    const res = await fetch(skinUrl, { mode: "cors" });
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = `${name || "skin"}.png`;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(skinUrl, "_blank", "noopener");
  }
}

export function SkullOwnerHost() {
  const toast = useToast();
  const { t } = useTranslation();
  const [skin] = createResource(owner, resolveSkin);

  const displayName = () => skin()?.name ?? owner()?.name ?? null;
  const uuid = () => skin()?.uuid ?? owner()?.id ?? null;

  const copyUuid = () => {
    const value = uuid();
    if (!value) return;
    navigator.clipboard?.writeText(value);
    toast?.info(t("SKULL_UUID_COPIED"));
  };

  return (
    <Show when={owner()}>
      <Modal
        open={true}
        onClose={() => setOwner(null)}
        maxWidth="34rem"
        title={<Text val="SKULL_OWNER_TITLE" />}
      >
        <div class={styles.body}>
          <Show
            when={!skin.loading}
            fallback={
              <div class={styles.center}>
                <i class="fa-solid fa-spinner fa-spin fa-2x" />
              </div>
            }
          >
            <Show
              when={skin()}
              fallback={
                <div class={styles.empty}>
                  <i class="fa-solid fa-user-slash" />
                  <p><Text val="SKULL_NO_OWNER" /></p>
                </div>
              }
            >
              {(resolved) => (
                <>
                  <div class={styles.viewer}>
                    <SkinViewer3D skinUrl={resolved().skinUrl} slim={resolved().model === "slim"} />
                  </div>
                  <Show
                    when={displayName()}
                    fallback={
                      <div class={styles.note}>
                        <i class="fa-solid fa-circle-info" />
                        <span><Text val="SKULL_CUSTOM_TEXTURE_INFO" /></span>
                      </div>
                    }
                  >
                    <div class={styles.fields}>
                      <div class={styles.field}>
                        <span class={styles.label}><Text val="SKULL_PLAYER_NAME" /></span>
                        <span class={styles.value}>{displayName()}</span>
                      </div>
                      <Show when={uuid()}>
                        <div class={styles.field}>
                          <span class={styles.label}><Text val="SKULL_UUID" /></span>
                          <button type="button" class={styles.uuid} onClick={copyUuid} title={t("SKULL_COPY_UUID")}>
                            {uuid()}
                            <i class="fa-solid fa-copy" />
                          </button>
                        </div>
                      </Show>
                    </div>
                  </Show>
                  <button
                    type="button"
                    class="btn btn-primary"
                    onClick={() => downloadSkin(resolved().skinUrl, displayName() ?? "skin")}
                  >
                    <i class="fa-solid fa-download" /> <Text val="SKULL_DOWNLOAD_SKIN" />
                  </button>
                </>
              )}
            </Show>
          </Show>
        </div>
      </Modal>
    </Show>
  );
}
